// src/components/Login.js
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  saveEmployeeId,
  verifyEmailCode,
  loginWithPassword,
  getLogoutNoticeMessage,
  clearLogoutNotice,
} from "../utils/auth";
import "./Login.css";
import appLogo from "../icons/app_white_logo.svg";
import emailIcon from "../icons/email_input.svg";
import codeIcon from "../icons/password_input.svg";
import { useRoleActions } from "../context/RoleContext";
import { API_URL } from "../config";

// Генерация/хранение постоянного идентификатора устройства
function getOrCreateDeviceId() {
  try {
    let id = localStorage.getItem("device_id");
    if (!id) {
      id =
        (crypto && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem("device_id", id);
    }
    return id;
  } catch {
    // fallback, если localStorage недоступен
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

// Локальная "чистая" функция для шага 1 (без apiFetch, без credentials)
async function requestEmailCode(email, deviceId) {
  const res = await fetch(`${API_URL}/auth/email/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit", // важно: на экране логина токенов нет
    body: JSON.stringify({ email, device_id: deviceId }),
  });

  if (res.status === 428) {
    // сервер требует согласие — фронт должен открыть модалку
    const data = await res.json().catch(() => ({}));
    const err = new Error(data?.detail || "agreement_required");
    err.__agreement_required = true;
    throw err;
  }

  // пользователь заблокирован — покажем подсказку под e-mail
  if (res.status === 423) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data?.detail || "user_blocked");
    err.__user_blocked = true;
    throw err;
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.detail || "Не удалось отправить код");
  }

  return await res.json(); // { ok: true }
}

const Login = () => {
  const [email, setEmail] = useState("");
  const [confirmedEmail, setConfirmedEmail] = useState("");
  const [step2, setStep2] = useState(false);
  const [code, setCode] = useState("");

  // По умолчанию сохраняем существующий вход по e-mail-коду
  const [loginMode, setLoginMode] = useState("email_code");
  const [password, setPassword] = useState("");

  const [sending, setSending] = useState(false);
  const [error, setError] = useState(() => getLogoutNoticeMessage());

  // ошибка под полем e-mail
  const [emailError, setEmailError] = useState("");

  // состояние модалки соглашения
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);

  const dismissLogoutNotice = () => {
    clearLogoutNotice();
  };

  const deviceId = useMemo(getOrCreateDeviceId, []);
  const navigate = useNavigate();
  const { refresh: refreshRole } = useRoleActions();

  // Шаг 1: запросить код на почту
  const handleSendCode = async (e) => {
    e.preventDefault();
    dismissLogoutNotice();
    setError("");
    setEmailError("");
    setSending(true);

    try {
      const mail = email.trim();

      await requestEmailCode(mail, deviceId);

      // фиксируем адрес, на который реально ушёл код
      setConfirmedEmail(mail);

      // Если отправка прошла — переключаемся на шаг ввода кода
      setStep2(true);
    } catch (err) {
      // если требуется согласие — открываем модалку
      if (err && err.__agreement_required) {
        setPendingEmail(email);
        setAgreementOpen(true);
      } else if (err && err.__user_blocked) {
        setEmailError("пользователь заблокирован администратором");
      } else {
        setError(err.message || "Не удалось отправить код");
      }
    } finally {
      setSending(false);
    }
  };

  // Шаг 2: верифицировать код → сохранить токены →
  // загрузить роль/employee_id → редирект
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    dismissLogoutNotice();
    setError("");
    setSending(true);

    try {
      const mail = (confirmedEmail || email).trim();

      // verifyEmailCode внутри utils сохранит access/refresh токены
      await verifyEmailCode(mail, code);

      // теперь можно безопасно тянуть профиль/роль
      await Promise.allSettled([
        saveEmployeeId(),
        refreshRole?.(),
      ]);

      navigate("/plus", { replace: true });
    } catch (err) {
      setError(err.message || "Код недействителен");
    } finally {
      setSending(false);
    }
  };

  // Вход по e-mail и паролю → сохранить токены →
  // загрузить роль/employee_id → редирект
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    dismissLogoutNotice();
    setError("");
    setEmailError("");
    setSending(true);

    try {
      // loginWithPassword внутри utils сохранит access/refresh токены
      await loginWithPassword(email, password);

      await Promise.allSettled([
        saveEmployeeId(),
        refreshRole?.(),
      ]);

      navigate("/plus", { replace: true });
    } catch (err) {
      setError(err.message || "Не удалось выполнить вход");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <img
          src={appLogo}
          alt="Login applogo"
          className="login-banner"
        />

        <form
          onSubmit={
            loginMode === "password"
              ? handlePasswordLogin
              : step2
                ? handleVerifyCode
                : handleSendCode
          }
          className="login-form"
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              onClick={() => {
                dismissLogoutNotice();
                setLoginMode("email_code");
                setPassword("");
                setError("");
                setEmailError("");
              }}
              style={{
                appearance: "none",
                border:
                  loginMode === "email_code"
                    ? "1px solid #BD23DB"
                    : "1px solid #e5e7eb",
                background:
                  loginMode === "email_code"
                    ? "#BD23DB"
                    : "#fff",
                color:
                  loginMode === "email_code"
                    ? "#fff"
                    : "#111",
                padding: "10px 12px",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              По коду
            </button>

            <button
              type="button"
              onClick={() => {
                dismissLogoutNotice();
                setLoginMode("password");

                // Сбрасываем состояние второго шага входа по коду
                setStep2(false);
                setCode("");
                setConfirmedEmail("");

                // Закрываем модалку соглашения, если она была открыта
                setAgreementOpen(false);
                setPendingEmail(null);

                setError("");
                setEmailError("");
              }}
              style={{
                appearance: "none",
                border:
                  loginMode === "password"
                    ? "1px solid #BD23DB"
                    : "1px solid #e5e7eb",
                background:
                  loginMode === "password"
                    ? "#BD23DB"
                    : "#fff",
                color:
                  loginMode === "password"
                    ? "#fff"
                    : "#111",
                padding: "10px 12px",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              По паролю
            </button>
          </div>

          <label htmlFor="email">E-mail</label>

          <div className="input-wrapper">
            <img
              src={emailIcon}
              alt=""
              className="input-icon"
            />

            <input
              type="email"
              id="email"
              placeholder="Email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError("");
              }}
              required
              autoComplete="username"
              disabled={
                loginMode === "email_code" && step2
              }
            />
          </div>

          {/* красная подпись под полем e-mail */}
          {emailError && (
            <div
              style={{
                color: "red",
                marginTop: 6,
                marginBottom: 6,
                fontSize: 12,
              }}
            >
              {emailError}
            </div>
          )}

          {loginMode === "password" && (
            <>
              <label htmlFor="password">Пароль</label>

              <div className="input-wrapper">
                <img
                  src={codeIcon}
                  alt=""
                  className="input-icon"
                />

                <input
                  type="password"
                  id="password"
                  placeholder="Пароль"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  required
                  autoComplete="current-password"
                />
              </div>
            </>
          )}

          {loginMode === "email_code" && step2 && (
            <>
              <label htmlFor="code">Код из письма</label>

              <div className="input-wrapper">
                <img
                  src={codeIcon}
                  alt=""
                  className="input-icon"
                />

                <input
                  type="text"
                  id="code"
                  inputMode="numeric"
                  placeholder="6-значный код"
                  value={code}
                  maxLength={6}
                  onChange={(e) => {
                    setCode(
                      e.target.value.replace(/\D/g, "")
                    );
                  }}
                  required
                  autoFocus
                />
              </div>
            </>
          )}

          {error && (
            <p
              className="login-error"
              role="alert"
              aria-live="polite"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            className="login-submit"
            disabled={sending}
          >
            {loginMode === "password"
              ? sending
                ? "Входим…"
                : "Войти"
              : step2
                ? sending
                  ? "Проверяем…"
                  : "Войти"
                : sending
                  ? "Отправляем…"
                  : "Получить код"}
          </button>

          {loginMode === "email_code" && step2 && (
            <button
              type="button"
              style={{
                marginTop: 10,
                alignSelf: "center",
                background: "none",
                border: "none",
                color: "#BD23DB",
                cursor: "pointer",
              }}
              onClick={() => {
                dismissLogoutNotice();
                setStep2(false);
                setCode("");
                setError("");
                setEmailError("");
              }}
              title="Отправить код повторно"
            >
              Отправить код повторно
            </button>
          )}
        </form>
      </div>

      {/* Модалка соглашения — открывается только если бэкенд вернул 428 */}
      {agreementOpen && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.45)",
            display: "grid",
            placeItems: "center",
            zIndex: 9999,
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-card"
            style={{
              background: "#fff",
              width: "min(720px, 92vw)",
              maxHeight: "86vh",
              borderRadius: 12,
              boxShadow: "0 12px 40px rgba(0,0,0,.2)",
              display: "grid",
              gridTemplateRows: "auto 1fr auto",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "14px 16px",
                borderBottom: "1px solid #eee",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 800,
                }}
              >
                Подтверждение входа с нового устройства
              </h3>
            </div>

            <div
              className="modal-body"
              style={{
                padding: 16,
                overflowY: "auto",
                whiteSpace: "pre-wrap",
                lineHeight: 1.35,
                color: "#111",
              }}
            >
              {`Вы впервые используете это устройство для доступа в корпоративное приложение. Подтверждая вход, вы подтверждаете, что устройство принадлежит вам и несёте ответственность за все дальнейшие действия, выполненные с него.
            `}
            </div>

            <div
              className="modal-actions"
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                padding: "12px 16px",
                borderTop: "1px solid #eee",
                background: "#fafafa",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setAgreementOpen(false);
                  setPendingEmail(null);
                }}
                style={{
                  appearance: "none",
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  padding: "10px 14px",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                Отмена
              </button>

              <button
                type="button"
                onClick={async () => {
                  setError("");
                  setSending(true);

                  try {
                    const mail = (
                      pendingEmail ?? email
                    ).trim();

                    const res = await fetch(
                      `${API_URL}/auth/email/agreements/accept`,
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          email: mail,
                          device_id: deviceId,
                        }),
                      }
                    );

                    if (!res.ok) {
                      const data = await res
                        .json()
                        .catch(() => ({}));

                      throw new Error(
                        data?.detail ||
                          "Не удалось принять соглашение"
                      );
                    }

                    // Сразу отправляем код и переходим к шагу 2
                    await requestEmailCode(mail, deviceId);

                    setConfirmedEmail(mail);
                    setStep2(true);
                    setAgreementOpen(false);
                    setPendingEmail(null);
                  } catch (e) {
                    setError(
                      e.message || "Ошибка согласия"
                    );
                  } finally {
                    setSending(false);
                  }
                }}
                style={{
                  appearance: "none",
                  border: "1px solid #BD23DB",
                  background: "#BD23DB",
                  color: "#fff",
                  padding: "10px 14px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Принять
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;